import {
  Box,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-mui";
import { useState } from "react";
import { api, type UserGroup } from "../api/client.ts";
import { keys } from "../api/queryKeys.ts";

interface GroupRef {
  id: string;
  name: string;
}

interface UserGroupChipsProps {
  orgId: string;
  userId: string;
  /** For accessibility labels only. */
  userName: string;
  /** Groups this user already belongs to (from the members query). */
  memberGroups: GroupRef[];
  /** All groups in the org — the "add existing" candidate source. */
  allGroups: UserGroup[];
  canManage: boolean;
}

/**
 * Inline per-user group management for an org member row: chips of the groups the
 * user belongs to (removable), plus a "+" menu to add an existing group or create a
 * new one (created in the current org and this user added to it). Group data comes
 * from the parent members query; mutations invalidate both the user-group
 * namespace and the members cache so chips refresh in place.
 */
function UserGroupChips({
  orgId,
  userId,
  userName,
  memberGroups,
  allGroups,
  canManage,
}: UserGroupChipsProps) {
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const closeMenu = () => {
    setAnchorEl(null);
    setCreating(false);
    setNewName("");
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: keys.userGroup.all });
    queryClient.invalidateQueries({ queryKey: keys.organizationMembership.byOrg(orgId) });
  };

  const addExisting = useMutation({
    mutationFn: (groupId: string) => api.addUserGroupMember(orgId, groupId, userId),
    onSuccess: () => {
      invalidate();
      closeMenu();
    },
  });

  const remove = useMutation({
    mutationFn: (groupId: string) => api.removeUserGroupMember(orgId, groupId, userId),
    onSuccess: invalidate,
  });

  const createAndAdd = useMutation({
    mutationFn: async (name: string) => {
      const group = await api.createUserGroup(orgId, { name });
      await api.addUserGroupMember(orgId, group.id, userId);
    },
    onSuccess: () => {
      invalidate();
      closeMenu();
    },
  });

  const memberIds = new Set(memberGroups.map((g) => g.id));
  const candidates = allGroups.filter((g) => !memberIds.has(g.id));

  const submitCreate = () => {
    const name = newName.trim();
    if (name) createAndAdd.mutate(name);
  };

  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
      {memberGroups.map((g) => (
        <Chip
          key={g.id}
          size="small"
          variant="outlined"
          label={g.name}
          onDelete={canManage ? () => remove.mutate(g.id) : undefined}
        />
      ))}
      {canManage ? (
        <>
          <Chip
            size="small"
            variant="outlined"
            icon={<Plus />}
            label={memberGroups.length ? undefined : "Add"}
            aria-label={`Add ${userName} to a group`}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              borderStyle: "dashed",
              color: "text.secondary",
              "& .MuiChip-icon": { color: "inherit", fontSize: 14, mx: memberGroups.length ? 0.75 : undefined },
              "& .MuiChip-label:empty": { display: "none" },
            }}
          />
          <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeMenu}>
            {candidates.map((g) => (
              <MenuItem key={g.id} onClick={() => addExisting.mutate(g.id)}>
                {g.name}
              </MenuItem>
            ))}
            {candidates.length > 0 ? <Divider /> : null}
            {creating ? (
              <Box sx={{ px: 2, py: 1 }}>
                <TextField
                  autoFocus
                  size="small"
                  label="New group name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitCreate();
                    }
                  }}
                  disabled={createAndAdd.isPending}
                />
              </Box>
            ) : (
              <MenuItem onClick={() => setCreating(true)}>Create new group…</MenuItem>
            )}
          </Menu>
        </>
      ) : null}
    </Stack>
  );
}

export default UserGroupChips;
